import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { Check } from 'lucide-react'

import { Button } from '@mdxui/primitives'

import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  reducedMotionVariants,
} from '../../lib/motion'

/**
 * Props for CtaCentered component
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Centered layout with optional border
 * - Two variants: 'default' (bordered, muted bg) or 'minimal' (no border/bg)
 * - Two buttons: primary + secondary (1-2 regular buttons)
 * - **NEVER use this for install commands - use CtaCenteredCheck instead**
 * - Optional background pattern (rendered at 80% opacity)
 * - Best for simple, focused CTAs
 *
 * **CTA Button Rules (CRITICAL):**
 * 1. Use this component for 1-2 regular buttons (primary, optional secondary)
 * 2. Use CtaCenteredCheck for npm/npx/pnpm install commands (InstallSnippet ONLY)
 * 3. NEVER mix InstallSnippet with regular buttons - they are mutually exclusive
 */
export interface CtaCenteredProps {
  /** Main heading text */
  heading?: string
  /** Description text below heading */
  description?: string
  /** Optional checklist items displayed horizontally with checkmarks */
  items?: string[]
  /** Button configuration */
  buttons?: {
    primary?: {
      text: string
      url: string
    }
    secondary?: {
      text: string
      url: string
    }
  }
  /** Visual variant: 'default' has border+bg, 'minimal' has no border/bg */
  variant?: 'default' | 'minimal'
  /** Optional background pattern (rendered inside card at 50% opacity) */
  background?: React.ReactNode
  /** Additional CSS classes */
  className?: string
}

/**
 * CtaCentered - Centered CTA with border
 *
 * **Layout:**
 * ```
 * ┌────────────────────────────────────────┐
 * │  ┌──────────────────────────────────┐  │
 * │  │           Heading                │  │
 * │  │          Description             │  │
 * │  │     [Primary]  [Secondary]       │  │
 * │  └──────────────────────────────────┘  │
 * └────────────────────────────────────────┘
 * ```
 *
 * **Best Use Cases:**
 * - Simple focused CTAs
 * - Trial signup sections
 * - Newsletter subscriptions
 *
 * @example
 * ```tsx
 * <CtaCentered
 *   heading="Start building faster"
 *   description="Try our tools with a 14-day free trial."
 *   buttons={{
 *     primary: { text: 'Get Started', url: '/signup' },
 *     secondary: { text: 'Learn More', url: '/docs' },
 *   }}
 * />
 *
 * // With background pattern
 * <CtaCentered
 *   heading="Start building faster"
 *   description="Try our tools with a 14-day free trial."
 *   background={<GridDashedTop />}
 *   buttons={{
 *     primary: { text: 'Get Started', url: '/signup' },
 *     secondary: { text: 'Learn More', url: '/docs' },
 *   }}
 * />
 * ```
 */
const CtaCentered = ({
  heading = 'Start building your websites faster',
  description = 'Try our tools and services to build your website faster. Start with a 14-day free trial. No credit card required. No setup fees. Cancel anytime.',
  items,
  buttons = {
    primary: { text: 'Get Started', url: '#' },
    secondary: { text: 'Learn More', url: '#' },
  },
  variant = 'default',
  background,
  className,
}: CtaCenteredProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const contentAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : headerVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-16 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        <div
          className={cn(
            'relative overflow-hidden flex flex-col items-center justify-center py-16 text-center md:py-20',
            variant === 'default' && 'rounded-[var(--radius-xl,calc(var(--radius)*1.5))] border border-border bg-muted/50',
          )}
        >
          {background && (
            <div className="absolute inset-0 opacity-50">{background}</div>
          )}
          <motion.div className="relative z-10 mx-auto max-w-3xl px-4" variants={contentAnimation}>
            <h2 className="mb-6 sm:mb-4 text-balance text-3xl font-semibold md:text-5xl">
              {heading}
            </h2>
            {description && (
              <Markdown size="base">{description}</Markdown>
            )}
            {items && items.length > 0 && (
              <ul className="mt-6 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-8">
                {items.map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2 text-sm font-medium text-muted-foreground"
                  >
                    <span className="flex size-5 items-center justify-center rounded-full bg-primary/10">
                      <Check className="size-3 text-primary" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-8 flex flex-col justify-center gap-2 sm:flex-row">
              {buttons?.primary && (
                <Button size="lg" asChild>
                  <a href={buttons.primary.url}>{buttons.primary.text}</a>
                </Button>
              )}
              {buttons?.secondary && (
                <Button size="lg" variant="outline" asChild>
                  <a href={buttons.secondary.url}>{buttons.secondary.text}</a>
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  )
}

export { CtaCentered }
